import React, { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import api from "../../services/axiosInstance";
import ReportPreviewModal from "./ReportPreviewModal";
import { buildPRPDF, downloadPRPDF } from "../../utils/prPdfUtils";

/**
 * Purchase Request preview / print modal.
 * Fetches PR details, generates a branded PDF via jsPDF, then shows it in the
 * shared ReportPreviewModal with preview + download actions.
 *
 * External API kept identical to the previous version:
 *   <PRPreviewModal prId={id} visible={visible} onCancel={...} />
 */
const PRPreviewModal = ({ prId, visible, onCancel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfDataUri, setPdfDataUri] = useState(null);

  const fetchPRData = useCallback(async () => {
    setLoading(true);
    setPdfDataUri(null);
    try {
      const res = await api.get(`/inventory/pr/get.php?id=${prId}`);
      if (res.data && res.data.status === "success") {
        setData(res.data);
        try {
          const doc = await buildPRPDF(res.data);
          setPdfDataUri(doc.output("bloburl"));
        } catch (pdfError) {
          console.error("PDF generation failed:", pdfError);
          message.error("Failed to generate PDF preview.");
        }
      } else {
        message.error("Failed to load Purchase Request details.");
      }
    } catch (error) {
      message.error("Error loading Purchase Request.");
    } finally {
      setLoading(false);
    }
  }, [prId]);

  useEffect(() => {
    if (visible && prId) {
      fetchPRData();
    } else if (!visible) {
      setData(null);
      setPdfDataUri(null);
    }
  }, [visible, prId, fetchPRData]);

  const handleDownload = async () => {
    try {
      if (!data) return;
      await downloadPRPDF(data);
      message.success("PDF downloaded successfully.");
    } catch (error) {
      console.error("PDF download failed:", error);
      message.error("Failed to download PDF.");
    }
  };

  return (
    <ReportPreviewModal
      visible={visible}
      onCancel={onCancel}
      title={`Purchase Request - ${data?.master?.pr_ref || ""}`}
      pdfDataUri={pdfDataUri}
      loading={loading}
      showFiltersOption={false}
      onDownload={handleDownload}
    />
  );
};

export default PRPreviewModal;
